import NewLayout from "@components/new-ui/Layout";
import React from "react";
import { CasestudyCard, CasestudyCardProps } from ".";
import { CaseStudyHerosection, CaseStudyInformation, FeatureList, Navbar, navLinks, OrganizationProfile } from "./alrannan-administrative-services";
import StatsInfo from "@components/new-ui/IndustriesSection/StatsInfo";

const casestudyCardsDetails: CasestudyCardProps[] = [
    {
        imageUrl: "https://assets.gallabox.com/gb-home/case-study/inlingua/inlingua.png",
        title: "Inlingua scales student engagement and doubles growth with Gallabox",
        sector: "Education",
        externalLink: "/case-study/inlingua",
    },
    {
        imageUrl: "https://assets.gallabox.com/gb-home/case-study/nura/nura-hero.png",
        title: "Success Story : Improved Transparency and Increased Conversions",
        sector: "Healthcare",
        externalLink: "/case-study/nura",
    },
    {
        imageUrl: "https://assets.gallabox.com/gb-home/case-study/alrannan-admin-service.png",
        title: "Success Story : Improved Transparency and Increased Conversions",
        sector: "Education",
        externalLink: "/case-study/alrannan-administrative-services",
    },
];

const KhivrajElixir: React.FC = () => {
    return (
        <NewLayout
            title="Khivraj Elixir accelerates lead conversion with Gallabox | Case Study"
            description="Discover how Khivraj Elixir streamlined customer communication, automated lead nurturing, and achieved 70% business growth by integrating Gallabox's chatbot and WhatsApp solutions."
            canonical="https://gallabox.com/case-study/khivraj-elixir"
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/case-study/khivraj-elixir/khivraj-elixir-og.png" }}
        >
            <CaseStudyHerosection
                title="Khivraj Elixir shifts gears on customer engagement with Gallabox"
                imageUrl={"https://assets.gallabox.com/gb-home/case-study/khivraj-elixir/khivraj-elixir.png"}
                redirectUrl={"/case-study"}
            />
            <StatsInfo
                statsInfo={[
                    { percentage: "70%", label: "overall business growth" },
                    { percentage: "50%", label: "increase in lead generation" },
                    { percentage: "30%", label: "boost in employee productivity" },
                ]}
            />

            <section className="max-w-7xl mx-auto  md:grid md:grid-cols-6 mt-24 mb-24">
                <div className="bg-white col-span-1">
                    <div style={{ position: "sticky", top: "100px" }}>
                        <OrganizationProfile
                            companyProfileData={[{ label: "Category", value: ["Automobiles"] }]}
                            product={{
                                title: "Features Used",
                                productLink: [
                                    { link: "https://gallabox.com/whatsapp-chatbot", label: "Chatbots" },
                                    { link: "https://gallabox.com", label: "WhatsApp Website Widgets" },
                                    { link: "https://gallabox.com", label: "WhatsApp Forms" },
                                    { link: "https://gallabox.com/whatsapp-flows", label: "WhatsApp Flows" },
                                    { link: "https://gallabox.com/whatsapp-broadcast", label: "Broadcast" },
                                ],
                            }}
                        />
                    </div>
                </div>
                <div className=" col-span-4 md:pl-8 px-4">
                    <div className="flex flex-col gap-y-12 ">
                        <section id="About Khivraj Elixir">
                            <CaseStudyInformation subTitle={"About Khivraj Elixir Pvt. Ltd."} className="mt-0" />
                            <p className="mt-2">
                                Khivraj Elixir Pvt. Ltd., part of the 80-year-strong House of Khivraj, is rooted in a tradition of scale, adaptability, and trust. Over the decades,
                                the group has diversified across industries — from real estate and energy to fertilizers and clean mobility.
                            </p>
                            <p className="mt-5">
                                In the automobile dealerships business, Khivraj has become a household name by representing some of the most recognized brands in the industry,
                                including Triumph, KTM, Husqvarna, Force Gurkha, and Citiline, among others. With a portfolio spanning motorcycles, passenger cars, and commercial
                                vehicles, the company has built deep credibility with customers who associate the Khivraj name with reliability and premium service.
                            </p>
                            <p className="mt-5">
                                As digital traffic and customer expectations continue to rise, Khivraj Elixir realized that its lead management process had to evolve to match the
                                speed and immediacy of modern automotive journeys.
                            </p>
                        </section>

                        <section id="Challenges">
                            <CaseStudyInformation subTitle={"Challenges before Gallabox"} className="mt-0" />

                            <p className="mt-2">
                                Like many players in the automobile sector, Khivraj Elixir was grappling with a challenge that feels familiar to most dealerships today: leads were
                                coming in, but the conversations weren&apos;t happening fast enough.
                            </p>
                            <FeatureList
                                List={[
                                    {
                                        listItem: "Cold leads due to slow follow-ups",
                                        nestedListItem: [
                                            "Customers browsing online would often leave their details on forms. However, by the time the sales team reached out manually via a phone call, many had already lost interest — or worse, shifted to a competitor.",
                                        ],
                                    },
                                    {
                                        listItem: "Manual effort draining the team",
                                        nestedListItem: [
                                            "Sales executives had to spend hours tracking, dialing, and re-dialing numbers only to hit unanswered calls. Instead of actively closing deals, their time went into chasing leads that quickly went cold.",
                                        ],
                                    },
                                    {
                                        listItem: "Lack of real-time engagement",
                                        nestedListItem: [
                                            "Car buyers often want details at that very moment — whether it's a quick finance query, model availability, or test drive request. Without instant responses, Khivraj was missing opportunities at critical moments of intent.",
                                        ],
                                    },
                                ]}
                                className="mt-4 bg-gray-100 "
                            />
                            <CaseStudyInformation subTitle={"Initial solutions tried"} className="mt-12" />
                            <p className="mt-2">
                                Before switching to Gallabox, Khivraj relied on manual website form responses and call follow-ups. While this seemed workable at first, the results
                                weren&apos;t consistent. Leads slipped through the cracks, and no matter how diligent the team was, the lack of automation meant customers
                                weren&apos;t getting the seamless experience they expected.
                            </p>
                            <p className="mt-5">
                                They even evaluated other automation platforms like Aisensy. But the available solutions felt either too restrictive, too technical, or didn&apos;t
                                offer the flexibility the team needed.
                            </p>
                        </section>

                        <section id="Solutions">
                            <CaseStudyInformation subTitle={"Why Khivraj chose Gallabox"} className="mt-0" />
                            <p className="mt-2">
                                Khivraj was clear — their goal wasn&apos;t to just bolt on another tool. They needed something smarter, intuitive, and actually usable by the sales
                                team. Gallabox stood out for three reasons:
                            </p>

                            <FeatureList
                                List={[
                                    {
                                        listItem: "Practical solutions, no pushy sales pitch:",
                                        nestedListItem: [
                                            "When I first explored Gallabox, the demo itself convinced me. The team didn&apos;t hard sell — they just showed me how it could solve my problem,' says Narayanan M from Khivraj Elixir.",
                                        ],
                                    },
                                    {
                                        listItem: "WhatsApp-first engagement:",
                                        nestedListItem: [
                                            "Instead of depending only on calls, the chatbot and WhatsApp workflows let Khivraj meet customers right where they are. Conversations could start instantly — before leads went cold.",
                                        ],
                                    },
                                    {
                                        listItem: "Efficiency without complexity:",
                                        nestedListItem: [
                                            "From chatbots to WhatsApp forms and broadcast features, everything was under one roof, easy to adopt, and didn't need constant tech support.",
                                        ],
                                    },
                                ]}
                                className="mt-4 bg-gray-100 "
                            />
                            <CaseStudyInformation subTitle={"Goals with Gallabox"} className="mt-12" />
                            <p className="mt-2">Khivraj set out with two focused objectives:</p>
                            <FeatureList
                                List={[
                                    { listItem: "Streamline customer communication – consolidate conversations into one seamless channel." },
                                    { listItem: "Automate customer support – reduce the strain on employees by letting chatbots handle repetitive queries." },
                                ]}
                                className="mt-4 bg-gray-100 "
                            />
                        </section>

                        <section id="Impact">
                            <CaseStudyInformation subTitle={"Impact on their business growth"} className="mt-0" />
                            <p className="mt-2">The results were not just positive — they were transformative:</p>

                            <FeatureList
                                List={[
                                    {
                                        listItem: "70% overall business growth, a direct outcome of faster, smarter engagement.",
                                    },
                                    {
                                        listItem: "50% increase in lead generation, with fewer inquiries going cold.",
                                    },
                                    {
                                        listItem: "70% higher customer satisfaction, thanks to instant and consistent responses.",
                                    },
                                    {
                                        listItem: "30% boost in employee productivity, as repetitive tasks shifted to automation.",
                                    },
                                ]}
                                className="mt-5 bg-gray-100"
                            />

                            <p className="mt-5">
                                For a dealership managing inquiries across vehicle types and customer journeys, these changes translated to smoother workflows, happier customers,
                                and more meaningful conversions.
                            </p>

                            <CaseStudyInformation subTitle={"Shifting gears for the future"} className="mt-10" />
                            <p className="mt-2">
                                For Khivraj Elixir, Gallabox is more than just a tool — it&apos;s a partner in redefining how they engage with today&apos;s digital-first automobile
                                customers. By ensuring that no lead goes unattended, their team can now focus more energy on conversations that close deals rather than chasing
                                contacts that go silent.
                            </p>
                            <p className="mt-5">
                                Looking ahead, Khivraj plans to continue leveraging Gallabox&apos;s capabilities to push automation even deeper into their customer journey. From
                                instant finance clarifications to test drive scheduling reminders, the goal is simple: make every interaction easy, timely, and customer-first.
                            </p>
                            <p className="mt-5">
                                With Gallabox as its co-driver, Khivraj Elixir is steering toward a future where customer engagement isn&apos;t just faster — it&apos;s
                                frictionless.
                            </p>
                        </section>
                    </div>
                </div>
                <div>
                    <div style={{ position: "sticky", top: "100px", minHeight: "510px" }} className="hidden md:block">
                        <Navbar links={navLinks} />
                    </div>
                </div>
            </section>
            <div className="max-w-6xl mx-auto">
                <section>
                    <h3 className="text-gray-900 mb-10 mt-5 text-left ml-4">Check out other customer stories</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto gap-10 mb-20">
                        {casestudyCardsDetails?.map((data, index) => (
                            <CasestudyCard key={index} {...data} />
                        ))}
                    </div>
                </section>
            </div>
        </NewLayout>
    );
};

export default KhivrajElixir;
